import { Chart, registerables } from "chart.js";
import "chartjs-adapter-date-fns";
Chart.register(...registerables);

export const MiniGraphOptions = (
  extractedIndicatorTimes,
  wellArrays,
  yValues,
  showFiltered
) => {
  // Efficient min/max calculation for indicatorTimes
  let indicatorTimes = Object.values(extractedIndicatorTimes);
  let minXValue = Infinity;
  let maxXValue = -Infinity;
  for (const arr of indicatorTimes) {
    for (const t of arr) {
      if (t < minXValue) minXValue = t;
      if (t > maxXValue) maxXValue = t;
    }
  }
  if (!isFinite(minXValue)) minXValue = 0;
  if (!isFinite(maxXValue)) maxXValue = 100;

  // Use all wells for min/max calculation
  const wellsToUse = wellArrays;

  // Efficient min/max calculation for allYValues. Reads the appropriate
  // typed-array directly so iterating every well doesn't force {x,y}[]
  // materialization, which would OOM on large datasets.
  let minYValue = Infinity;
  let maxYValue = -Infinity;
  for (const well of wellsToUse) {
    for (const indicator of well.indicators) {
      if (!indicator.isDisplayed) continue;
      const ys = showFiltered ? indicator.filteredYs : indicator.rawYs;
      if (ys) {
        for (let i = 0; i < ys.length; i++) {
          const y = ys[i];
          if (y < minYValue) minYValue = y;
          if (y > maxYValue) maxYValue = y;
        }
        continue;
      }
      const data = showFiltered
        ? indicator.filteredData
        : indicator.rawData ?? [];
      if (Array.isArray(data)) {
        for (const point of data) {
          if (!point) continue;
          if (point.y < minYValue) minYValue = point.y;
          if (point.y > maxYValue) maxYValue = point.y;
        }
      }
    }
  }
  if (!isFinite(minYValue)) minYValue = 0;
  if (!isFinite(maxYValue)) maxYValue = 100;

  // Use static values for decimation
  const samples = 20;
  const threshold = 80;

  return {
    normalized: true,
    maintainAspectRatio: true,
    responsive: true,
    // DPR capped to 1: 96–384 tiny canvases × 2× retina is significant
    // memory + per-frame draw cost; visual delta on ~100px charts is
    // negligible.
    devicePixelRatio: 1,

    spanGaps: false,
    events: ["onHover"],
    // `false` (not `{ duration: 0 }`) so chart.js skips animator setup
    // entirely. Compounds across 96–384 mini-chart instances.
    animation: false,
    parsing: false,
    plugins: {
      legend: false,
      decimation: {
        enabled: true,
        algorithm: "lttb",
        samples: samples,
        threshold: threshold,
      },
      tooltip: {
        enabled: false, // set to FALSE if using an external function for tooltip
        mode: "nearest",
        intersect: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
      line: {
        borderWidth: 1.5,
      },
    },
    layout: {
      autoPadding: false,
      padding: {
        left: -30,
        bottom: -30,
      },
    },
    scales: {
      x: {
        type: "linear",
        min: minXValue,
        max: maxXValue,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: minYValue,
        max: maxYValue,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
  };
};
